// The local FE's two-level device menu, prefixed onto a site's Manage mount.
// Shared by the desktop sidebar (AppLayout) and the phone hamburger dropdown
// (AppTopbar): cloud nav on top, divider with the site name, then the SAME
// sections/sub-items as the local app (single-source model in
// @inverter/device-ui nav/deviceNav). Entries without synced-data pages —
// or History when the plan/opt-in doesn't sync it — render disabled.
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  deviceNavSections,
  isPathActive,
} from "@inverter/device-ui/src/nav/deviceNav";
import { RefreshCw } from "../components/icons";
import { api } from "../api/api";
import { asApiError, get, isUpgradeRequired, post } from "../api/client";

/** Status of the most recent full-update request (GET/POST
 *  /api/sites/{id}/device/full-update). */
interface FullUpdate {
  command_id: number | null;
  status: "queued" | "acked" | "failed" | "expired" | "refused" | null;
  requested_at: number | null;
  completed_at: number | null;
  /** The device's own detail on a failure. */
  detail: string | null;
  queued?: boolean;
}

// Device pages available over synced data (the DeviceShell route table).
const MOUNTED_DEVICE_ROUTES = [
  "/dashboard",
  "/history",
  "/optimization",
  "/billing",
  "/optimization-settings",
  "/general",
  "/controller/logs",   // the alert log only (DeviceShell)
];

function deviceRouteAvailable(to?: string): boolean {
  if (!to) return false;
  return MOUNTED_DEVICE_ROUTES.some((p) => to === p || to.startsWith(`${p}/`));
}

// Pages that render synced HISTORY data — optional twice over (owner plan
// must include data sync AND the local device's Data opt-in must be on).
function deviceRouteNeedsHistory(to?: string): boolean {
  if (!to) return false;
  return to.startsWith("/history") || to === "/optimization/history";
}

/** Ask the device to push everything now, and show where that request got to.
 *
 *  Opening these pages already queues one (routes/site_device.device_session),
 *  so this normally starts blue on its own and turns green when the device has
 *  pushed — the button is for asking again without leaving the page.
 *
 *  Colours are the state: idle muted, in-flight blue, done green. */
function FullUpdateButton({ siteId }: { siteId: number }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["full-update", siteId],
    queryFn: () => get<FullUpdate>(`/api/sites/${siteId}/device/full-update`),
    // 3 s while a request is in flight (the device is on the 5 s session
    // cadence then, so it resolves in seconds); otherwise a slow heartbeat —
    // opening these pages queues a full update from the SHELL, and this menu
    // renders outside it, so without the heartbeat the control could read the
    // status before that request exists and never show it.
    refetchInterval: (q) => (q.state.data?.status === "queued" ? 3_000 : 15_000),
    retry: false,
  });
  const [denied, setDenied] = useState<null | "role" | "plan">(null);
  const [askError, setAskError] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: () => post<FullUpdate>(`/api/sites/${siteId}/device/full-update`),
    onSuccess: (d) => {
      setDenied(null);
      setAskError(null);
      qc.setQueryData(["full-update", siteId], d);
    },
    // A viewer may watch but not queue, and a Free plan has no command
    // delivery at all — both are quiet states on the control, not throws.
    // Only THOSE two answers are; anything else (offline server, a 404 for a
    // site without a device) is reported as what it is, not as a role issue.
    onError: (e) => {
      const err = asApiError(e);
      if (isUpgradeRequired(e)) setDenied("plan");
      else if (err.code === "insufficient_site_role") setDenied("role");
      else {
        setDenied(null);
        setAskError(err.message);
      }
    },
  });

  // `expired` means the device never came back for it — the offline case, and
  // the one worth showing: silently falling back to idle would read as "no
  // request was ever made".
  const state = ask.isPending || status.data?.status === "queued" ? "pending"
    : askError ? "error"
      : status.data?.status === "acked" ? "done"
        : status.data?.status === "failed" || status.data?.status === "refused" ? "failed"
          : status.data?.status === "expired" ? "expired" : "idle";
  const tone =
    denied ? "text-muted"
      : state === "pending" ? "text-brand"
      : state === "done" ? "text-good"
        : state === "failed" || state === "expired" || state === "error" ? "text-bad"
          : "text-muted hover:text-text";
  const label =
    denied === "plan" ? t("device.fullUpdate.upgrade", { defaultValue: "Requesting an update needs a Basic plan or above" })
      : denied === "role" ? t("device.fullUpdate.denied", { defaultValue: "Operator role required" })
      : state === "pending" ? t("device.fullUpdate.pending", { defaultValue: "Full update requested — waiting for the device…" })
        : state === "done" ? t("device.fullUpdate.done", { defaultValue: "Device pushed a full update" })
          : state === "error" ? [t("device.fullUpdate.error", { defaultValue: "Could not request an update" }), askError].filter(Boolean).join(" — ")
          : state === "failed" ? [t("device.fullUpdate.failed", { defaultValue: "The device could not complete the update" }), status.data?.detail].filter(Boolean).join(" — ")
            : state === "expired" ? t("device.fullUpdate.expired", { defaultValue: "The device never picked the request up — it may be offline" })
              : t("device.fullUpdate.ask", { defaultValue: "Request a full update from the device" });

  return (
    <button
      type="button"
      onClick={() => ask.mutate()}
      disabled={state === "pending"}
      title={label}
      aria-label={label}
      className={`shrink-0 rounded-full p-1 transition ${tone} ${state === "pending" ? "cursor-default" : ""}`}
    >
      <RefreshCw size={14} className={state === "pending" ? "animate-spin" : undefined} />
    </button>
  );
}


/** Match a location against the device-area mount; null outside it. */
export function deviceSiteIdFromPath(pathname: string): number | null {
  const m = pathname.match(/^\/app\/sites\/(\d+)\/device(?:\/|$)/);
  return m ? Number(m[1]) : null;
}

export function DeviceNavMenu({
  siteId,
  pathname,
  collapsed = false,
}: {
  siteId: number;
  pathname: string;
  collapsed?: boolean;
}) {
  const { t } = useTranslation();
  const base = `/app/sites/${siteId}/device`;
  const rel = pathname.startsWith(base) ? pathname.slice(base.length) || "/" : "/";

  // Device translations load with the (lazy) device area; merging here too
  // lets the menu render translated even before DeviceShell mounts.
  const [bundlesReady, setBundlesReady] = useState(false);
  useEffect(() => {
    let on = true;
    void import("./i18nBridge").then((m) => {
      m.mergeDeviceBundles();
      if (on) setBundlesReady(true);
    });
    return () => {
      on = false;
    };
  }, []);

  const sites = useQuery({ queryKey: ["sites"], queryFn: api.sites, staleTime: 30_000 });
  const site = sites.data?.find((s) => s.id === siteId);

  // History availability comes from the device meta (owner plan + local
  // Data opt-in). Treat "still loading" as available to avoid a flicker.
  const meta = useQuery({
    queryKey: ["device-meta", siteId],
    queryFn: () =>
      get<{ capabilities?: { history?: boolean } }>(`/api/sites/${siteId}/device/meta`),
    staleTime: 30_000,
    retry: 1,
  });
  const historyOk = meta.data?.capabilities?.history !== false;
  const available = (to?: string) =>
    deviceRouteAvailable(to) && (historyOk || !deviceRouteNeedsHistory(to));

  const sections = deviceNavSections.map((section) => ({
    ...section,
    // No local-admin concept here; admin-only entries (Users) stay local.
    subItems: section.subItems.filter((item) => !item.adminOnly),
  }));
  const activeSection =
    sections.find((section) => section.subItems.some((item) => isPathActive(rel, item.to))) ??
    sections[0];

  const itemClass = (active: boolean) =>
    `nav-item relative ${active ? "active" : ""} ${collapsed ? "justify-center" : ""}`;
  const disabledClass = `nav-item opacity-55 cursor-default ${collapsed ? "justify-center" : ""}`;

  return (
    <div className="flex flex-col gap-1 min-h-0" data-ready={bundlesReady}>
      {/* Divider + the site this menu manages. */}
      <div className="border-t border-border my-1" />
      <div className={`flex items-center gap-1 ${collapsed ? "justify-center" : "px-3"}`}>
        <div className="label truncate grow" title={site?.name ?? `Site ${siteId}`}>
          {collapsed ? "·" : site?.name ?? `Site ${siteId}`}
        </div>
        {/* Keyed by site: the denied/error state belongs to ONE site and
            must not carry over when the menu switches to another. */}
        <FullUpdateButton key={siteId} siteId={siteId} />
      </div>

      <div className="flex flex-col gap-1">
        {sections.map((section) => {
          const first = section.subItems.find((item) => available(item.to));
          const label = t(section.labelKey, { defaultValue: section.labelDefault });
          if (!first?.to) {
            return (
              <div key={section.id} className={disabledClass} aria-disabled
                title={collapsed ? label : undefined}>
                <section.icon size={18} className="shrink-0" />
                {!collapsed && <span>{label}</span>}
              </div>
            );
          }
          return (
            <NavLink
              key={section.id}
              to={`${base}${first.to}`}
              className={itemClass(section.id === activeSection.id)}
              title={collapsed ? label : undefined}
            >
              <section.icon size={18} className="shrink-0" />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          );
        })}
      </div>

      <div className="border-t border-border my-1" />

      <div className="flex-1 overflow-y-auto pr-1 space-y-1 min-h-0">
        {activeSection.subItems.map((item) => {
          const label = t(item.labelKey, { defaultValue: item.labelDefault });
          if (!available(item.to) || !item.to) {
            return (
              <div key={`${activeSection.id}-${item.labelKey}-${item.labelDefault}`}
                className={disabledClass} aria-disabled
                title={collapsed ? label : undefined}>
                <item.icon size={18} className="shrink-0" />
                {!collapsed && <span>{label}</span>}
              </div>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={`${base}${item.to}`}
              end={item.end}
              className={({ isActive }) =>
                `nav-item relative ${isActive ? "active" : ""} ${collapsed ? "justify-center" : ""}`
              }
              title={collapsed ? label : undefined}
            >
              <item.icon size={18} className="shrink-0" />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}
